import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { pagePreviewApi, type CarrierIdentity, type PageIR, type PagePanel, type PagePreviewItem } from '@/api/pagePreviewApi'
import { friendlyMessage } from '@/api/http'
import { Button } from '@/components/ui/button'
import { clampBox, containViewport, displayDeltaToNormalized, dragBox, normalizedToPage, pageToDisplay, type Box, type DragHandle, type Viewport } from '@/lib/diegeticGeometry'

interface CarrierGroup { key: string; panel: PagePanel; carrier: string; box: Box; lines: string[]; fallback: boolean }
interface ExpectedCarrier extends CarrierIdentity { box: Box | null }

function carrierGroups(ir: PageIR): CarrierGroup[] {
  const groups = new Map<string, CarrierGroup>()
  for (const text of ir.diegetic_texts ?? []) {
    const panel = ir.panels.find(panel => panel.id === text.panel_id)
    if (!panel || !panel.asset_id || panel.rect_mm[2] <= 0 || panel.rect_mm[3] <= 0) continue
    const key = JSON.stringify([text.panel_id, text.carrier])
    const group = groups.get(key)
    if (group) group.lines.push(text.text)
    else groups.set(key, { key, panel, carrier: text.carrier, box: clampBox(text.carrier_box ?? { x: 0.25, y: 0.25, w: 0.5, h: 0.5 }), lines: [text.text], fallback: text.carrier_box == null })
  }
  return [...groups.values()]
}

function matchesOverrides(ir: PageIR, expected: ExpectedCarrier[]) {
  return expected.every(item => {
    const panel = ir.panels.find(panel => panel.asset_id === item.panel_image_asset_id)
    const texts = (ir.diegetic_texts ?? []).filter(text => text.panel_id === panel?.id && text.carrier === item.carrier)
    return texts.length > 0 && texts.every(text => item.box === null
      ? text.placement !== 'manual'
      : text.placement === 'manual' && text.carrier_box != null && (['x', 'y', 'w', 'h'] as const).every(key => Math.abs(text.carrier_box![key] - item.box![key]) < 1e-6))
  })
}

function pause(signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const abort = () => { clearTimeout(timer); reject(new Error('aborted')) }
    const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve() }, 1500)
    signal.addEventListener('abort', abort, { once: true })
    if (signal.aborted) abort()
  })
}

// There is no public page-compose job GET. Observe the resulting page/IR instead.
async function composeAndRefresh(chapterId: string, page: PagePreviewItem, expected: ExpectedCarrier[], signal: AbortSignal) {
  await pagePreviewApi.compose(chapterId)
  for (let attempt = 0; attempt < 80 && !signal.aborted; attempt++) {
    const pages = await pagePreviewApi.list(chapterId)
    const current = pages.find(item => item.page_index === page.page_index)
    if (current) {
      const ir = await pagePreviewApi.ir(chapterId, current.asset_id)
      if (matchesOverrides(ir, expected)) return pages
    }
    await pause(signal)
  }
  throw new Error('page_refresh_timeout')
}

const corners = ['nw', 'ne', 'sw', 'se'] as const
const cornerClass = {
  nw: '-left-1 -top-1 cursor-nwse-resize', ne: '-right-1 -top-1 cursor-nesw-resize',
  sw: '-left-1 -bottom-1 cursor-nesw-resize', se: '-right-1 -bottom-1 cursor-nwse-resize',
}

export function DiegeticEditor({ chapterId, page, ir, onClose, onRefresh }: {
  chapterId: string; page: PagePreviewItem; ir: PageIR; onClose: () => void; onRefresh: (pages: PagePreviewItem[]) => void
}) {
  const { t } = useTranslation()
  const groups = useMemo(() => carrierGroups(ir), [ir])
  const [drafts, setDrafts] = useState<Record<string, Box>>({})
  const [viewport, setViewport] = useState<Viewport>({ x: 0, y: 0, scale: 0 })
  const [failedImage, setFailedImage] = useState(false)
  const [phase, setPhase] = useState<'write' | 'compose'>('write')
  const surface = useRef<HTMLDivElement>(null)
  const controller = useRef<AbortController | null>(null)
  const drag = useRef<{ group: CarrierGroup; box: Box; x: number; y: number; handle: DragHandle; viewport: Viewport } | null>(null)

  useEffect(() => {
    const abort = new AbortController()
    controller.current = abort
    return () => { abort.abort(); drag.current = null }
  }, [])

  const measure = () => {
    const rect = surface.current?.getBoundingClientRect()
    if (rect) setViewport(containViewport(ir.page, rect))
  }

  useEffect(() => {
    const resize = () => {
      drag.current = null
      const rect = surface.current?.getBoundingClientRect()
      if (rect) setViewport(containViewport(ir.page, rect))
    }
    const move = (event: MouseEvent) => {
      const active = drag.current
      if (!active) return
      const delta = displayDeltaToNormalized(event.clientX - active.x, event.clientY - active.y, active.group.panel.rect_mm, active.viewport)
      setDrafts(previous => ({ ...previous, [active.group.key]: dragBox(active.box, delta.x, delta.y, active.handle) }))
    }
    const end = () => { drag.current = null }
    const observer = new ResizeObserver(resize)
    if (surface.current) observer.observe(surface.current)
    resize()
    window.addEventListener('resize', resize)
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', end)
    window.addEventListener('blur', end)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', resize)
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', end)
      window.removeEventListener('blur', end)
    }
  }, [ir])

  const apply = useMutation({
    mutationFn: async (reset: boolean) => {
      const signal = controller.current!.signal
      drag.current = null
      setPhase('write')
      const expected: ExpectedCarrier[] = []
      for (const group of groups) {
        if (signal.aborted) throw new Error('aborted')
        if (!reset && !drafts[group.key]) continue
        const identity = { panel_image_asset_id: group.panel.asset_id, carrier: group.carrier }
        const box = reset ? null : clampBox(drafts[group.key])
        if (box) await pagePreviewApi.putOverride(chapterId, { ...identity, ...box })
        else await pagePreviewApi.deleteOverride(chapterId, identity)
        expected.push({ ...identity, box })
      }
      if (signal.aborted) throw new Error('aborted')
      setPhase('compose')
      const pages = await composeAndRefresh(chapterId, page, expected, signal)
      if (!signal.aborted) { onRefresh(pages); onClose() }
    },
  })

  function start(event: ReactMouseEvent, group: CarrierGroup, handle: DragHandle) {
    if (event.button !== 0 || apply.isPending || viewport.scale <= 0) return
    event.preventDefault(); event.stopPropagation()
    const box = drafts[group.key] ?? group.box
    drag.current = { group, box, x: event.clientX, y: event.clientY, handle, viewport }
    setDrafts(previous => ({ ...previous, [group.key]: box }))
  }

  return (
    <div className="space-y-3" aria-label={t('diegeticEditor.title')}>
      <p className="text-sm text-muted-foreground">{t('diegeticEditor.help')}</p>
      {groups.some(group => group.fallback) ? <p className="text-sm text-muted-foreground">{t('diegeticEditor.fallback')}</p> : null}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={apply.isPending || failedImage || !Object.keys(drafts).length} onClick={() => apply.mutate(false)}>{t('diegeticEditor.save')}</Button>
        <Button size="sm" variant="outline" disabled={apply.isPending || !groups.length} onClick={() => apply.mutate(true)}>{t('diegeticEditor.reset')}</Button>
        <Button size="sm" variant="ghost" disabled={apply.isPending} onClick={onClose}>{t('diegeticEditor.cancel')}</Button>
      </div>
      {apply.isPending ? <p role="status" className="text-sm">{t(phase === 'write' ? 'diegeticEditor.saving' : 'diegeticEditor.composing')}</p> : null}
      {apply.isError ? <p role="alert" className="text-sm">{friendlyMessage(apply.error, t(phase === 'write' ? 'diegeticEditor.saveFailed' : 'diegeticEditor.composeFailed'))}</p> : null}
      {failedImage ? <p role="alert">{t('pagePreview.imageFailed')}</p> : null}
      <div ref={surface} data-testid="diegetic-surface" className="relative mx-auto w-full max-w-3xl select-none" style={{ aspectRatio: `${ir.page.width_mm} / ${ir.page.height_mm}` }}>
        <img src={pagePreviewApi.imageUrl(chapterId, page.asset_id)} alt={t('pagePreview.page', { page: page.page_index, total: page.page_count })} draggable={false} onLoad={measure} onError={() => setFailedImage(true)} className="absolute inset-0 h-full w-full object-contain object-center" />
        {!failedImage && viewport.scale > 0 ? groups.map((group, index) => {
          const box = drafts[group.key] ?? group.box
          const display = pageToDisplay(normalizedToPage(box, group.panel.rect_mm), viewport)
          return (
            <div key={group.key} data-testid="carrier-box" role="group" aria-label={t('diegeticEditor.carrier', { number: index + 1 })}
              onMouseDown={event => start(event, group, 'move')}
              className="absolute cursor-move border-2 border-foreground bg-background/40 text-foreground focus-within:ring-2 focus-within:ring-ring"
              style={{ left: display.x, top: display.y, width: display.w, height: display.h }}>
              <button type="button" disabled={apply.isPending} aria-label={t('diegeticEditor.move', { number: index + 1 })}
                className="absolute inset-0 flex h-full w-full flex-col overflow-hidden text-left focus-visible:outline-none"
                onKeyDown={event => {
                  const direction = { ArrowLeft: [-0.01, 0], ArrowRight: [0.01, 0], ArrowUp: [0, -0.01], ArrowDown: [0, 0.01] }[event.key]
                  if (!direction) return
                  event.preventDefault()
                  setDrafts(previous => ({ ...previous, [group.key]: dragBox(box, direction[0], direction[1], event.shiftKey ? 'se' : 'move') }))
                }}>
                {group.lines.map((line, lineIndex) => <span key={lineIndex} className="flex min-h-0 flex-1 items-center overflow-hidden px-1 text-xs"><span className="truncate bg-background text-foreground">{line}</span></span>)}
              </button>
              {corners.map(corner => <button key={corner} type="button" tabIndex={-1} disabled={apply.isPending} data-corner={corner} aria-label={t(`diegeticEditor.${corner}`)} onMouseDown={event => start(event, group, corner)} className={`absolute size-3 border border-foreground bg-background hover:bg-muted ${cornerClass[corner]}`} />)}
            </div>
          )
        }) : null}
      </div>
    </div>
  )
}
